import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useTranslation } from 'react-i18next'
import { Button } from '@components'
import { Filters } from '@haus-storefront-react/filters'
import { type EnabledFilter, type FacetValueFilterType } from '@haus-storefront-react/shared-types'
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons'
import { type CSSPropertiesWithCustomVars } from '@utils/css-vars'
import styles from './FacetFiltersDropdown.module.scss'

/**
 * CSS variables the Button component expects for base and state styling.
 * Maps facet filter dropdown design tokens so Button's built-in hover/active/disabled rules apply.
 */
const filterButtonStyle: CSSPropertiesWithCustomVars = {
  '--haus-button-background-color':
    'var(--haus-facetFiltersDropdown__filterButton__background, transparent)',
  '--haus-button-text-color':
    'var(--haus-facetFiltersDropdown__filterButton__color, var(--haus-color-body))',
  '--haus-button-border-color': 'transparent',
  '--haus-button-border-style': 'none',
  '--haus-button-border-width': '0',
  '--haus-button-border-radius': 'var(--haus-facetFiltersDropdown__filterButton__border-radius, 0)',
  '--haus-button-font-size': 'var(--haus-facetFiltersDropdown__filterButton__font-size, 16px)',
  '--haus-button-font-weight': 'var(--haus-facetFiltersDropdown__filterButton__font-weight, 700)',
  '--haus-button-text-decoration': 'none',
  '--haus-button-padding': 'var(--haus-facetFiltersDropdown__filterButton__padding, 0)',
  '--haus-button-text-align': 'start',
  '--haus-button-hover-background-color':
    'var(--haus-facetFiltersDropdown__filterButton__hover__background, transparent)',
  '--haus-button-hover-text-color':
    'var(--haus-facetFiltersDropdown__filterButton__hover__color, var(--haus-color-body))',
  '--haus-button-hover-border-color': 'transparent',
  '--haus-button-hover-border-style': 'none',
  '--haus-button-hover-font-weight':
    'var(--haus-facetFiltersDropdown__filterButton__hover__font-weight, 700)',
  '--haus-button-hover-text-decoration': 'none',
  '--haus-button-active-background-color':
    'var(--haus-facetFiltersDropdown__filterButton__hover__background, transparent)',
  '--haus-button-active-text-color':
    'var(--haus-facetFiltersDropdown__filterButton__hover__color, var(--haus-color-body))',
  '--haus-button-active-border-color': 'transparent',
  '--haus-button-active-border-style': 'none',
  '--haus-button-active-font-weight':
    'var(--haus-facetFiltersDropdown__filterButton__hover__font-weight, 700)',
  '--haus-button-active-text-decoration': 'none',
  '--haus-button-disabled-background-color':
    'var(--haus-facetFiltersDropdown__filterButton__background, transparent)',
  '--haus-button-disabled-text-color':
    'var(--haus-facetFiltersDropdown__filterButton__color, var(--haus-color-body))',
  '--haus-button-disabled-border-color': 'transparent',
  '--haus-button-disabled-border-style': 'none',
  '--haus-button-disabled-font-weight':
    'var(--haus-facetFiltersDropdown__filterButton__font-weight, 700)',
  '--haus-button-disabled-text-decoration': 'none',
}

interface FacetFiltersDropdownsProps {
  facetFilter: EnabledFilter<FacetValueFilterType>
  toggleFacetValue: (facetCode: string, valueId: string) => void
  onFilterAnnounce?: (message: string) => void
}

export const FacetFiltersDropdown = ({
  facetFilter,
  toggleFacetValue,
  onFilterAnnounce,
}: FacetFiltersDropdownsProps) => {
  const { t } = useTranslation()
  return (
    <DropdownMenu.Root modal>
      <Filters.Group facetCode={facetFilter.facetCode}>
        {(facet) => (
          <>
            {facet.values.length > 0 && (
              <DropdownMenu.Trigger asChild>
                <Button
                  aria-label={facet.label}
                  className={styles.filterButton}
                  style={filterButtonStyle}
                  useCustomStyles={true}
                >
                  {facet.label}
                  <ChevronDownIcon width={16} height={16} />
                </Button>
              </DropdownMenu.Trigger>
            )}
            <DropdownMenu.Content sideOffset={6} align="start" className={styles.dropdownContent}>
              <div className={styles.filtersGroupContent}>
                {facet.values.map((facetValue) => (
                  <DropdownMenu.CheckboxItem
                    key={facetValue.id}
                    checked={!!facetValue.checked}
                    onCheckedChange={() => {
                      const isAdding = !facetValue.checked
                      toggleFacetValue(facet.facetCode, facetValue.id)
                      if (onFilterAnnounce) {
                        onFilterAnnounce(
                          isAdding
                            ? t('a11y.facetValueFilter.ariaLive.filterAdded', {
                                filterValue: facetValue.name,
                              })
                            : t('a11y.facetValueFilter.ariaLive.filterRemoved', {
                                filterValue: facetValue.name,
                              }),
                        )
                      }
                    }}
                    className={styles.checkboxItem}
                  >
                    <div
                      className={styles.checkboxRoot}
                      data-state={facetValue.checked && 'checked'}
                    >
                      <DropdownMenu.ItemIndicator className={styles.checkboxIndicator}>
                        <CheckIcon width={20} height={20} />
                      </DropdownMenu.ItemIndicator>
                    </div>
                    {facetValue.name}
                  </DropdownMenu.CheckboxItem>
                ))}
              </div>
            </DropdownMenu.Content>
          </>
        )}
      </Filters.Group>
    </DropdownMenu.Root>
  )
}

export default FacetFiltersDropdown
